import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { countries } from '@/lib/seo/countries'
import { states, type StateStatus } from '@/lib/seo/states'

export const Route = createFileRoute('/online-poker/countries/$country/')({
  loader: ({ params }) => {
    const country = countries.find((c) => c.slug === params.country)
    if (!country) throw notFound()
    return country
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    return {
      meta: [
        { title: `Online Poker in ${loaderData.name} | Powka` },
        {
          name: 'description',
          content: `Online poker culture, community, and cities in ${loaderData.name}. Discover how players across ${loaderData.name} enjoy the game.`,
        },
        {
          property: 'og:title',
          content: `Online Poker in ${loaderData.name} | Powka`,
        },
        {
          property: 'og:description',
          content: `Online poker culture and community in ${loaderData.name}.`,
        },
        { property: 'og:type', content: 'article' },
      ],
    }
  },
  component: RouteComponent,
})

const statusDot: Record<StateStatus, string> = {
  legal: 'bg-green-500',
  'gray-area': 'bg-yellow-500',
  illegal: 'bg-red-500',
}

const statusConfig: Record<StateStatus, { label: string; className: string }> =
  {
    legal: {
      label: 'Legal & Regulated',
      className:
        'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20',
    },
    'gray-area': {
      label: 'Unregulated',
      className:
        'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20',
    },
    illegal: {
      label: 'Prohibited',
      className:
        'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20',
    },
  }

function RouteComponent() {
  const country = Route.useLoaderData()
  const isUS = country.slug === 'united-states'

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span>/</span>
        <Link to="/online-poker" className="hover:text-foreground">
          Online Poker
        </Link>
        <span>/</span>
        <Link to="/online-poker/countries" className="hover:text-foreground">
          Countries
        </Link>
        <span>/</span>
        <span className="text-foreground">{country.name}</span>
      </nav>

      <h1 className="text-3xl font-bold mb-4">
        Online Poker in {country.name}
      </h1>

      <p className="text-muted-foreground leading-relaxed mb-10">
        {country.summary}
      </p>

      <div className="mb-12">
        <h2 className="text-lg font-semibold mb-4">About the Poker Scene</h2>
        <ul className="space-y-3">
          {country.keyFacts.map((fact, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
              <span className="text-muted-foreground">{fact}</span>
            </li>
          ))}
        </ul>
      </div>

      {isUS ? (
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Browse by State</h2>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {(
                Object.entries(statusConfig) as [
                  StateStatus,
                  (typeof statusConfig)[StateStatus],
                ][]
              ).map(([key, cfg]) => (
                <span key={key} className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${statusDot[key]}`} />
                  {cfg.label}
                </span>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {states.map((state) => (
              <Link
                key={state.slug}
                to="/online-poker/$state"
                params={{ state: state.slug }}
                className="flex items-center justify-between rounded-md border px-4 py-3 hover:bg-secondary/50 transition-colors"
              >
                <span className="font-medium">{state.name}</span>
                <span
                  className={`h-2 w-2 rounded-full shrink-0 ${statusDot[state.status]}`}
                />
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-12">
          <h2 className="text-lg font-semibold mb-4">
            Online Poker by City in {country.name}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {country.cities.map((city) => (
              <Link
                key={city.slug}
                to="/online-poker/countries/$country/$city"
                params={{ country: country.slug, city: city.slug }}
                className="rounded-md border px-4 py-3 text-sm font-medium hover:bg-secondary/50 transition-colors"
              >
                {city.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-secondary/30 px-6 py-5">
        <h2 className="font-semibold mb-1">Play on Powka</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Powka is a no-limit Texas Hold'em platform with a 1% rake. Deposits
          and withdrawals are handled in USDC — play from anywhere in the world.
        </p>
        <Link
          to="/"
          className="inline-block rounded-md bg-foreground text-background text-sm font-medium px-4 py-2 hover:opacity-90 transition-opacity"
        >
          Start playing
        </Link>
      </div>

      <div className="mt-10">
        <Link
          to="/online-poker/countries"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← View all countries
        </Link>
      </div>
    </div>
  )
}
